/**
 * El peer del share en vivo: la parte PURA (máquina de estados, backoff, cursor de señales,
 * calidad, tamaño del PiP, aviso de corte de sesión) y el envoltorio de RTCPeerConnection.
 *
 * Lo puro no toca el DOM ni RTCPeerConnection en el momento de importar: así `peer.test.ts` corre
 * en vitest/node. El envoltorio (`SharePeer`) solo usa los globales del navegador dentro de sus
 * métodos.
 *
 * Reglas del contrato (spec §4.3 y §4.8):
 *  - Negociación perfecta con UN solo ofertante: el sharer es "impolite" y es el ÚNICO que ofrece
 *    y reinicia ICE; el viewer es "polite" y solo contesta. Tras un handoff no chocan dos ofertas.
 *  - Half-trickle: el sharer manda la oferta con los candidatos ya reunidos (o a los 2 s), y lo que
 *    llegue tarde va como `ice`; el viewer contesta al toque y va goteando sus candidatos.
 *  - `failed` → `restartIce()` ×2; el tercero fuerza `iceTransportPolicy: 'relay'`; después se
 *    rinde con `ice_failed`.
 *  - Audio de IDA Y VUELTA (spec §4.10, 2026-09-04): el sharer arma SIEMPRE un transceiver de audio
 *    `sendrecv` (con o sin micrófono) y el viewer contesta por ese MISMO m-line con su mic: sin
 *    renegociar, sin que el viewer ofrezca nunca. El sharer reproduce la voz que llega por `onRemoteAudio`.
 */
import type { CandidatePair, PeerStats, Quality, SignalKind } from './types';

// ─────────────────────────── Backoff del socket ───────────────────────────

export const BACKOFF_BASE_MS = 500;
export const BACKOFF_MAX_MS = 8_000;
export const BACKOFF_JITTER_MS = 8_000;

/** 0,5·2ⁿ s con tope en 8 s, más 0-8 s de jitter: cien pestañas no vuelven todas en el mismo ms. */
export function backoffMs(attempt: number, rand: () => number = Math.random): number {
  const n = Number.isFinite(attempt) ? Math.max(0, Math.floor(attempt)) : 0;
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** n);
  const jitter = Math.max(0, Math.min(1, rand())) * BACKOFF_JITTER_MS;
  return Math.round(base + jitter);
}

// ─────────────────────────── Cursor de señales ───────────────────────────

/**
 * Las señales llegan por el socket Y por el replay de `GET /:id/signals?after=`: la misma fila puede
 * entrar dos veces (reintentos, reconexión). El cursor recuerda el último id aplicado y descarta lo
 * repetido o lo que ya quedó atrás.
 */
export type SignalCursor = {
  /** El id más alto aplicado: es el `after=` del replay. */
  lastId: number;
  /** Los últimos ids aplicados (tope 200): la dedupe real. */
  recent: number[];
};

export function newCursor(): SignalCursor { return { lastId: 0, recent: [] }; }

const CURSOR_RECENT_MAX = 200;

/**
 * ¿Se aplica esta señal? Devuelve el cursor avanzado (o el mismo si es repetida).
 *
 * La dedupe es por CONJUNTO de ids y no por "id <= último": medido en el e2e (2026-09-03), el
 * `answer` del viewer y su primer `ice` salen como dos POST que se cruzan; el ice sacó el id 83 y el
 * answer el 82, el socket entregó primero el 83, y con la marca de agua el answer quedaba descartado
 * para siempre — ICE conectaba pero DTLS nunca terminaba. Un id menor que llega tarde SE APLICA;
 * solo se descarta el que ya se aplicó.
 */
export function advanceCursor(cursor: SignalCursor, id: number): { apply: boolean; cursor: SignalCursor } {
  if (!Number.isFinite(id) || cursor.recent.includes(id)) return { apply: false, cursor };
  const recent = cursor.recent.length >= CURSOR_RECENT_MAX ? cursor.recent.slice(-(CURSOR_RECENT_MAX - 1)) : cursor.recent.slice();
  recent.push(id);
  return { apply: true, cursor: { lastId: Math.max(cursor.lastId, id), recent } };
}

// ─────────────────────────── Máquina de estados del peer ───────────────────────────

export type PeerRole = 'sharer' | 'viewer';
export type PeerPhase = 'new' | 'connecting' | 'live' | 'disconnected' | 'restarting' | 'failed' | 'closed';

export type PeerState = {
  phase: PeerPhase;
  /** Reinicios de ICE ya pedidos por el sharer (2 normales + 1 forzando relay). */
  iceRestarts: number;
  relayForced: boolean;
  /** Se conectó al menos una vez (el "connected" se reporta al servidor UNA vez). */
  everLive: boolean;
};

export type PeerEvent =
  | { type: 'connection'; state: RTCPeerConnectionState }
  | { type: 'grace' }      // pasaron los 3 s de gracia en 'disconnected'
  | { type: 'close' };

export type PeerAction =
  | 'none'
  | 'report-connected'   // primer 'connected': POST /:id/connected
  | 'start-grace'        // arrancar el timer de 3 s
  | 'cancel-grace'
  | 'restart-ice'        // sharer: pc.restartIce()
  | 'force-relay'        // sharer: setConfiguration({iceTransportPolicy:'relay'}) + restartIce()
  | 'give-up';           // ice_failed

export const MAX_ICE_RESTARTS = 2;
export const DISCONNECT_GRACE_MS = 3_000;

export function initialPeerState(): PeerState {
  return { phase: 'new', iceRestarts: 0, relayForced: false, everLive: false };
}

/** Qué hacer cuando ICE ya no levanta: solo el sharer reinicia; el viewer espera la re-oferta. */
function onFailed(s: PeerState, role: PeerRole): { state: PeerState; action: PeerAction } {
  if (role === 'viewer') return { state: { ...s, phase: 'restarting' }, action: 'none' };
  if (s.iceRestarts < MAX_ICE_RESTARTS) {
    return { state: { ...s, phase: 'restarting', iceRestarts: s.iceRestarts + 1 }, action: 'restart-ice' };
  }
  if (!s.relayForced) {
    return { state: { ...s, phase: 'restarting', iceRestarts: s.iceRestarts + 1, relayForced: true }, action: 'force-relay' };
  }
  return { state: { ...s, phase: 'failed' }, action: 'give-up' };
}

export function reducePeer(s: PeerState, ev: PeerEvent, role: PeerRole): { state: PeerState; action: PeerAction } {
  if (s.phase === 'closed' || s.phase === 'failed') return { state: s, action: 'none' };
  switch (ev.type) {
    case 'close':
      return { state: { ...s, phase: 'closed' }, action: 'none' };
    case 'grace':
      // Si en los 3 s volvió a 'connected' el timer se canceló; si seguimos desconectados, ICE no
      // se va a arreglar solo y se trata como 'failed'.
      if (s.phase !== 'disconnected') return { state: s, action: 'none' };
      return onFailed(s, role);
    case 'connection':
      switch (ev.state) {
        case 'connected': {
          const first = !s.everLive;
          const wasWaiting = s.phase === 'disconnected';
          return {
            state: { ...s, phase: 'live', everLive: true },
            action: first ? 'report-connected' : wasWaiting ? 'cancel-grace' : 'none',
          };
        }
        case 'connecting':
          return { state: { ...s, phase: s.phase === 'restarting' ? 'restarting' : 'connecting' }, action: 'none' };
        case 'disconnected':
          if (s.phase === 'disconnected') return { state: s, action: 'none' };
          return { state: { ...s, phase: 'disconnected' }, action: 'start-grace' };
        case 'failed':
          return onFailed(s, role);
        case 'closed':
          return { state: { ...s, phase: 'closed' }, action: 'none' };
        default:
          return { state: s, action: 'none' };
      }
    default:
      return { state: s, action: 'none' };
  }
}

// ─────────────────────────── Calidad a partir de getStats ───────────────────────────

export type QualityInput = {
  rttMs: number | null;
  fps: number | null;
  lossFraction: number | null;
  limitation: string | null;
};

/**
 * Un puntito de tres colores, no un dashboard: verde si se puede trabajar, ámbar si se nota,
 * rojo si no sirve. Los umbrales son de partida; F0 los ajusta con datos de Caracas y Manila.
 */
export function qualityFrom(i: QualityInput): Quality {
  const rtt = i.rttMs ?? 0;
  const loss = i.lossFraction ?? 0;
  const fps = i.fps;
  if (rtt > 400 || loss > 0.08 || (fps != null && fps < 4)) return 'poor';
  if (rtt > 200 || loss > 0.03 || (fps != null && fps < 12) || i.limitation === 'bandwidth') return 'fair';
  return 'good';
}

// ─────────────────────────── Tamaño de la ventana PiP ───────────────────────────

/**
 * Chrome recorta el Document PiP al 25 % del ÁREA de la pantalla (kMaxSiteRequestedWindowSizeRatio):
 * pedir 1280×720 en 1080p abre ~960×540 igual. Se pide directamente lo más grande que entra en ese
 * 25 % con relación 16:9, así el tamaño que abre es el que se pidió y no una sorpresa.
 */
export function pipSize(screenW: number, screenH: number, ratio = 0.25): { width: number; height: number } {
  const area = Math.max(0, screenW) * Math.max(0, screenH) * ratio;
  const height = Math.floor(Math.sqrt((area * 9) / 16));
  const width = Math.floor((height * 16) / 9);
  return { width: Math.max(320, width), height: Math.max(180, height) };
}

// ─────────────────────────── Aviso de corte de sesión ───────────────────────────

export const SESSION_WARN_MS = 5 * 60_000;

export type SessionWarning =
  | { kind: 'cut'; at: number }    // la regla de las 17:45: "Your session closes at {time}"
  | { kind: 'idle'; at: number };  // 30 min sin tocar nada fuera de horario: "click to stay signed in"

/**
 * El visor NO calcula la regla de corte: la manda el servidor en /state (`sessionCutAt` e
 * `idleLimitMs`, 0 = sin límite). Acá solo se resta contra la última interacción real del navegador
 * (`ppag:lastInput`) y se avisa cuando faltan 5 min o menos. Una fórmula (backend), un lector.
 */
export function sessionWarning(i: {
  sessionCutAt: string | null;
  idleLimitMs: number;
  lastInputAt: number;
  now: number;
}): SessionWarning | null {
  const cutAt = i.sessionCutAt ? Date.parse(i.sessionCutAt) : NaN;
  const idleAt = i.idleLimitMs > 0 ? i.lastInputAt + i.idleLimitMs : NaN;
  const candidates: SessionWarning[] = [];
  if (Number.isFinite(cutAt)) candidates.push({ kind: 'cut', at: cutAt });
  if (Number.isFinite(idleAt)) candidates.push({ kind: 'idle', at: idleAt });
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.at - b.at);
  const first = candidates[0]!;
  return first.at - i.now <= SESSION_WARN_MS ? first : null;
}

/** Segundos enteros que faltan hasta `expiresAt` (nunca negativos). */
export function secondsLeft(expiresAt: string, now: number): number {
  const t = Date.parse(expiresAt);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.ceil((t - now) / 1000));
}

/** mm:ss (o h:mm:ss) para el cronómetro del visor y de la píldora. */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Nombre del navegador para la guía de permisos de macOS; `null` si no se reconoce (el que llama traduce "tu navegador"). */
export function browserName(ua: string): string | null {
  if (/Edg\//.test(ua)) return 'Microsoft Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Google Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return null;
}

/** ¿Vale la pena repetir el POST de una señal? Solo el fallo de red/proxy (0, 502, 503, 504), nunca un 4xx. */
export function isSignalRetryable(e: unknown): boolean {
  const st = (e as { status?: unknown } | null)?.status;
  return st === 0 || st === 502 || st === 503 || st === 504;
}

// ─────────────────────────── Audio de ida y vuelta ───────────────────────────

/** Lo mínimo de un `RTCRtpTransceiver` que decide el plan (así se prueba sin navegador). */
export type TransceiverLike = { kind: string; direction: string; stopped?: boolean };

export type ViewerAudioPlan = {
  /** Índice del transceiver de audio que armó el sharer, o null si la oferta no trae audio (sharer viejo). */
  index: number | null;
  /** Se contesta 'sendrecv' aunque todavía no haya mic: el que llegue después entra por replaceTrack. */
  direction: 'sendrecv';
  /** Poner el track del micrófono en el sender (solo con mic). */
  replace: boolean;
};

/**
 * Qué hace el VIEWER con el audio al contestar una oferta (spec §4.10, ida y vuelta):
 *  - Toma el m-line de audio que el sharer armó (el primer transceiver de audio vivo). Sin m-line no
 *    hay nada que contestar: un sharer de la versión anterior ofrece `sendonly` y no hay vuelta.
 *  - Contesta `sendrecv` SIEMPRE, con o sin micrófono: si el mic llega después (permiso tardío, botón
 *    "Turn on my microphone", auricular enchufado) entra con `replaceTrack` sin renegociar, y como el
 *    viewer nunca ofrece, renegociar no sería posible desde su lado.
 *  - `replaceTrack` solo con mic; sin mic el sender queda vacío y no manda RTP.
 * El mute del viewer es `track.enabled=false`, nunca se para el track ni se toca la dirección.
 */
export function planViewerAudio(transceivers: readonly TransceiverLike[], hasMic: boolean): ViewerAudioPlan {
  const index = transceivers.findIndex((t) => t.kind === 'audio' && !t.stopped);
  if (index < 0) return { index: null, direction: 'sendrecv', replace: false };
  return { index, direction: 'sendrecv', replace: hasMic };
}

/** Esperas entre reintentos de una señal: 1 s, 3 s, 8 s (un swap de Railway tarda 20-40 s en total). */
export const SIGNAL_RETRY_MS: readonly number[] = [1_000, 3_000, 8_000];
/** Sin 'connected' en 20 s tras mandar la oferta/respuesta, se repite (la señal pudo perderse en el camino). */
export const CONNECT_TIMEOUT_MS = 20_000;
/** Tras 3 repeticiones sin conectar (60 s) se trata como 'failed' y entra la escalera de ICE. */
export const MAX_CONNECT_TIMEOUTS = 3;

// ─────────────────────────── Envoltorio de RTCPeerConnection ───────────────────────────

export type SendSignal = (kind: SignalKind, payload: Record<string, unknown>, toTabId: string | null) => Promise<void>;

export type SharePeerOptions = {
  role: PeerRole;
  iceServers: RTCIceServer[];
  maxBitrateKbps: number | null;
  /** Pestaña del otro lado: el sharer dirige sus señales al `viewerTabId`; el viewer al `sharerTabId`. */
  peerTabId: string | null;
  send: SendSignal;
  onPhase: (state: PeerState, action: PeerAction) => void;
  onStats: (s: PeerStats) => void;
  onRemoteStream: (stream: MediaStream) => void;
  /** Sharer: llegó la voz del viewer (ida y vuelta, §4.10); quien llama la reproduce en un <audio> oculto. */
  onRemoteAudio?: (stream: MediaStream) => void;
  /** Viewer: su micrófono, si ya lo tenía al construir el peer (también se puede dar después con `setMicTrack`). */
  micTrack?: MediaStreamTrack | null;
  /** Viewer: pasaron 20 s desde su `answer` sin conectar; quien llama repone lo que falte (replay de señales). */
  onStall?: () => void;
  /** Para los tests y el laboratorio: inyectar un constructor distinto. */
  connectionFactory?: (cfg: RTCConfiguration) => RTCPeerConnection;
  /** Para los tests: esperas de reintento distintas. */
  retryDelaysMs?: readonly number[];
};

const GATHER_TIMEOUT_MS = 2_000;
const STATS_EVERY_MS = 3_000;

export class SharePeer {
  private pc: RTCPeerConnection | null = null;
  private state: PeerState = initialPeerState();
  private opts: SharePeerOptions;
  private localStream: MediaStream | null = null;
  /** Viewer: el track del micrófono que viaja de vuelta al sharer (null = todavía sin mic). */
  private micTrack: MediaStreamTrack | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private graceTimer: number | null = null;
  private statsTimer: number | null = null;
  private relay = false;
  private hint: 'detail' | 'motion' = 'detail';
  private prevBytes: { t: number; bytes: number } | null = null;
  private closed = false;
  private makingOffer = false;
  private connectTimer: number | null = null;
  private connectTimeouts = 0;
  readonly startedAt = Date.now();

  constructor(opts: SharePeerOptions) {
    this.opts = opts;
    this.micTrack = opts.micTrack ?? null;
  }

  get phase(): PeerPhase { return this.state.phase; }
  get relayForced(): boolean { return this.relay; }
  get contentHint(): 'detail' | 'motion' { return this.hint; }

  /** El sharer le da la captura ANTES de conectar; se re-usa en cada reconstrucción del pc. */
  attach(stream: MediaStream): void {
    this.localStream = stream;
  }

  /**
   * Viewer: el micrófono llegó (o se fue). Si el peer ya contestó la oferta, entra en el mismo
   * transceiver de audio por `replaceTrack` (la dirección ya es sendrecv); si todavía no, lo toma la
   * próxima oferta. Nunca renegocia: el viewer no ofrece.
   */
  setMicTrack(track: MediaStreamTrack | null): void {
    this.micTrack = track;
    if (this.pc && this.opts.role === 'viewer') this.applyViewerAudio(this.pc);
  }

  /** El plan puro (`planViewerAudio`) aplicado al pc real. Idempotente. */
  private applyViewerAudio(pc: RTCPeerConnection): void {
    let list: RTCRtpTransceiver[];
    try { list = pc.getTransceivers(); } catch { return; }
    // `stopped` salió del lib de TS (deprecado) pero los navegadores lo siguen exponiendo; el spec nuevo lo dice por `currentDirection`.
    const plan = planViewerAudio(list.map((t) => ({
      kind: t.receiver.track.kind,
      direction: t.direction,
      stopped: (t as { stopped?: boolean }).stopped === true || (t.currentDirection as string | null) === 'stopped',
    })), !!this.micTrack);
    if (plan.index == null) return;
    const tx = list[plan.index]!;
    try { if (tx.direction !== plan.direction) tx.direction = plan.direction; } catch { /* transceiver parado */ }
    if (plan.replace && tx.sender.track !== this.micTrack) void tx.sender.replaceTrack(this.micTrack).catch(() => {});
  }

  /** Cambia el destinatario (handoff a otra pestaña) y reconstruye todo: nueva pc, nueva oferta. */
  retarget(peerTabId: string | null): void {
    this.opts.peerTabId = peerTabId;
    this.rebuild();
  }

  /** Credenciales TURN nuevas (viejas de más de 20 h) antes de un restart. */
  setIceServers(iceServers: RTCIceServer[]): void {
    this.opts.iceServers = iceServers;
    try { this.pc?.setConfiguration(this.config()); } catch { /* algunos navegadores no dejan cambiar en vivo */ }
  }

  private config(): RTCConfiguration {
    return { iceServers: this.opts.iceServers, iceTransportPolicy: this.relay ? 'relay' : 'all', bundlePolicy: 'max-bundle' };
  }

  private create(): RTCPeerConnection {
    const factory = this.opts.connectionFactory ?? ((cfg: RTCConfiguration) => new RTCPeerConnection(cfg));
    const pc = factory(this.config());
    pc.onconnectionstatechange = () => this.dispatch({ type: 'connection', state: pc.connectionState });
    pc.onicecandidate = (e) => {
      // Los candidatos que llegan DESPUÉS de mandar la oferta/respuesta gotean como 'ice'.
      if (e.candidate && this.sentLocalDescription) void this.safeSend('ice', e.candidate.toJSON() as Record<string, unknown>);
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      if (this.opts.role === 'sharer') {
        // Lo único que le llega al sharer es la voz del viewer por el m-line de audio (ida y vuelta).
        // Llega aunque el viewer no tenga mic (track mudo): reproducir silencio no cuesta nada.
        if (e.track.kind === 'audio') this.opts.onRemoteAudio?.(stream);
        return;
      }
      this.opts.onRemoteStream(stream);
    };
    if (this.opts.role === 'sharer') {
      pc.onnegotiationneeded = () => { void this.makeOffer(pc, false); };
      if (this.localStream) {
        for (const track of this.localStream.getVideoTracks()) {
          track.contentHint = this.hint;
          pc.addTrack(track, this.localStream);
        }
        // El m-line de audio existe SIEMPRE y es `sendrecv`, haya o no mic/sistema (spec §4.10, ida y
        // vuelta): así el viewer contesta con su micrófono por el MISMO transceiver y nadie renegocia.
        // Con `addTrack` un sharer sin mic no tendría m-line y el viewer no tendría por dónde hablar.
        // `streams` fija el msid: en el viewer el audio cae en el mismo MediaStream que el video.
        const audio = this.localStream.getAudioTracks()[0] ?? null;
        pc.addTransceiver(audio ?? 'audio', { direction: 'sendrecv', streams: [this.localStream] });
      }
    }
    return pc;
  }

  private sentLocalDescription = false;

  /** Arranca (o re-arranca) la conexión. El viewer solo se prepara y espera la oferta. */
  start(): void {
    if (this.closed) return;
    if (!this.pc) this.pc = this.create();
    this.startStats();
  }

  private rebuild(): void {
    if (this.closed) return;
    this.teardownPc();
    this.state = { ...initialPeerState(), everLive: this.state.everLive };
    this.pendingIce = [];
    this.sentLocalDescription = false;
    this.connectTimeouts = 0;
    this.pc = this.create();
    this.startStats();
  }

  private teardownPc(): void {
    if (this.graceTimer != null) { clearTimeout(this.graceTimer); this.graceTimer = null; }
    this.clearConnectTimer();
    if (this.pc) {
      this.pc.onconnectionstatechange = null;
      this.pc.onicecandidate = null;
      this.pc.ontrack = null;
      this.pc.onnegotiationneeded = null;
      try { this.pc.close(); } catch { /* ya cerrada */ }
      this.pc = null;
    }
  }

  private sendChain: Promise<void> = Promise.resolve();

  /**
   * Los envíos de ESTE lado salen EN SERIE: el POST siguiente arranca cuando el anterior ya está
   * insertado y avisado. Si salieran en paralelo, el `ice` que gotea justo después del `answer`
   * puede sacar un id mayor y llegar antes (medido), y el otro lado recibiría la respuesta después
   * del candidato. Con un solo remitente por dirección, el orden de ids = el orden de llegada.
   */
  private safeSend(kind: SignalKind, payload: Record<string, unknown>): Promise<void> {
    const to = this.opts.peerTabId;
    const next = this.sendChain.then(() => this.sendWithRetry(kind, payload, to));
    this.sendChain = next;
    return next;
  }

  /**
   * Una señal cuyo POST falla se perdía para siempre: `api.ts` no reintenta POST, y el replay por
   * `after=` solo repone lo que SÍ llegó al servidor. Si el `answer` (o la oferta) cae en el swap de
   * un deploy (502), el sharer se queda en `have-local-offer` sin remoto, ICE nunca arranca y la
   * conexión no pasa a 'failed' — los dos ven "Connecting…" para siempre. Se reintenta a 1, 3 y 8 s
   * solo ante fallo de red/proxy; si aun así no sale, se declara 'failed' y entra la escalera de ICE
   * (restart → relay → give-up), que es la que sabe cerrar la sesión.
   */
  private async sendWithRetry(kind: SignalKind, payload: Record<string, unknown>, to: string | null): Promise<void> {
    const delays = this.opts.retryDelaysMs ?? SIGNAL_RETRY_MS;
    for (let intento = 0; ; intento += 1) {
      if (this.closed) return;
      try { await this.opts.send(kind, payload, to); return; }
      catch (e) {
        if (!isSignalRetryable(e) || intento >= delays.length) {
          // Los candidatos sueltos no importan (el otro lado tiene los de la descripción); la
          // oferta/respuesta sí: sin ella no hay conexión que detectar.
          if (kind !== 'ice' && !this.closed) this.dispatch({ type: 'connection', state: 'failed' });
          return;
        }
        await new Promise((r) => setTimeout(r, delays[intento]));
      }
    }
  }

  // ── Plazo de conexión: si la señal se perdió en el camino, nadie la repone solo ──

  private clearConnectTimer(): void {
    if (this.connectTimer != null) { clearTimeout(this.connectTimer); this.connectTimer = null; }
  }

  private armConnectTimer(): void {
    this.clearConnectTimer();
    this.connectTimer = window.setTimeout(() => { this.connectTimer = null; void this.onConnectTimeout(); }, CONNECT_TIMEOUT_MS);
  }

  private async onConnectTimeout(): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;
    if (this.state.phase !== 'new' && this.state.phase !== 'connecting' && this.state.phase !== 'restarting') return;
    this.connectTimeouts += 1;
    if (this.connectTimeouts > MAX_CONNECT_TIMEOUTS) {
      this.dispatch({ type: 'connection', state: 'failed' });
      return;
    }
    if (this.opts.role === 'sharer') {
      // La oferta sigue sin respuesta: se re-manda tal cual (el viewer que ya la aplicó vuelve a
      // contestar; el que nunca la vio la recibe ahora). Si ya hubo respuesta, oferta nueva con ICE restart.
      if (pc.signalingState === 'have-local-offer' && pc.localDescription) {
        await this.safeSend('offer', { type: pc.localDescription.type, sdp: pc.localDescription.sdp });
      } else {
        await this.makeOffer(pc, true);
      }
    } else {
      this.opts.onStall?.();
    }
    // `this.phase` (getter) y no `this.state.phase`: tras los await el estado pudo cambiar y TS ya
    // había estrechado la propiedad por el early-return de arriba.
    if (!this.closed && this.phase !== 'live') this.armConnectTimer();
  }

  /** Espera a que ICE termine de reunir candidatos (half-trickle) o a los 2 s, lo que pase primero. */
  private waitGathering(pc: RTCPeerConnection): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => { pc.removeEventListener('icegatheringstatechange', check); clearTimeout(to); resolve(); };
      const check = () => { if (pc.iceGatheringState === 'complete') done(); };
      const to = setTimeout(done, GATHER_TIMEOUT_MS);
      pc.addEventListener('icegatheringstatechange', check);
    });
  }

  private async makeOffer(pc: RTCPeerConnection, iceRestart: boolean): Promise<void> {
    if (this.makingOffer || this.closed || pc !== this.pc) return;
    this.makingOffer = true;
    try {
      // Un restart con la oferta anterior todavía sin respuesta: se deshace (rollback) antes de
      // ofrecer de nuevo; si no, `signalingState` nunca vuelve a 'stable' y el restart no sale.
      if (iceRestart && pc.signalingState === 'have-local-offer') await pc.setLocalDescription({ type: 'rollback' });
      const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
      if (pc.signalingState !== 'stable' || pc !== this.pc) return;
      await pc.setLocalDescription(offer);
      await this.waitGathering(pc);
      if (pc !== this.pc || !pc.localDescription) return;
      this.sentLocalDescription = true;
      this.armConnectTimer();
      await this.safeSend(iceRestart ? 'ice-restart' : 'offer', { type: pc.localDescription.type, sdp: pc.localDescription.sdp });
      this.applyEncodingParams(pc);
    } catch { /* setLocalDescription puede fallar si se cerró en el medio: el estado lo refleja */ }
    finally { this.makingOffer = false; }
  }

  /** maxBitrate + mantener resolución: libwebrtc capa a 2,5 Mbps y el texto se lee más que el scroll. */
  private applyEncodingParams(pc: RTCPeerConnection): void {
    for (const sender of pc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      try {
        const p = sender.getParameters();
        if (!p.encodings || !p.encodings.length) p.encodings = [{}];
        if (this.opts.maxBitrateKbps) p.encodings[0]!.maxBitrate = this.opts.maxBitrateKbps * 1000;
        p.degradationPreference = this.hint === 'detail' ? 'maintain-resolution' : 'balanced';
        void sender.setParameters(p).catch(() => {});
      } catch { /* navegadores viejos sin setParameters */ }
    }
  }

  /**
   * Cap de subida por viewer (malla multi-espectador, spec §14): al agregar espectadores el
   * presupuesto de subida se reparte, así que cada peer recibe un `maxBitrate` menor. Idempotente:
   * el mismo valor no re-aplica setParameters. Solo tiene efecto del lado del sharer (es su codificador).
   */
  setMaxBitrate(kbps: number | null): void {
    if (this.opts.maxBitrateKbps === kbps) return;
    this.opts.maxBitrateKbps = kbps;
    if (this.pc && this.opts.role === 'sharer') this.applyEncodingParams(this.pc);
  }

  /** "Sharper text" ↔ "Smoother motion" (solo tiene efecto en el lado del sharer). */
  setContentHint(hint: 'detail' | 'motion'): void {
    this.hint = hint;
    for (const track of this.localStream?.getVideoTracks() ?? []) track.contentHint = hint;
    if (this.pc) this.applyEncodingParams(this.pc);
  }

  /** Señal entrante (del socket o del replay). El cursor de ids lo lleva quien llama. */
  async handleSignal(kind: SignalKind, payload: Record<string, unknown>): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;
    try {
      if (kind === 'offer' || kind === 'ice-restart') {
        if (this.opts.role !== 'viewer') return; // el sharer nunca recibe ofertas
        const desc = { type: 'offer', sdp: String(payload.sdp ?? '') } as RTCSessionDescriptionInit;
        // Viewer "polite": si por alguna razón tenía una oferta local, la deshace (rollback).
        if (pc.signalingState !== 'stable') {
          await Promise.all([pc.setLocalDescription({ type: 'rollback' }), pc.setRemoteDescription(desc)]);
        } else {
          await pc.setRemoteDescription(desc);
        }
        await this.flushIce(pc);
        // ANTES de la respuesta: el transceiver de audio de la oferta pasa a sendrecv y, si hay mic,
        // lleva el track (ida y vuelta). Después de `createAnswer` ya sería tarde para esta ronda.
        this.applyViewerAudio(pc);
        await pc.setLocalDescription(await pc.createAnswer());
        this.sentLocalDescription = true;
        this.armConnectTimer();
        if (pc.localDescription) await this.safeSend('answer', { type: pc.localDescription.type, sdp: pc.localDescription.sdp });
      } else if (kind === 'answer') {
        if (this.opts.role !== 'sharer') return;
        if (pc.signalingState !== 'have-local-offer') return; // respuesta vieja de una oferta ya superada
        await pc.setRemoteDescription({ type: 'answer', sdp: String(payload.sdp ?? '') });
        await this.flushIce(pc);
      } else if (kind === 'ice') {
        const cand = payload as RTCIceCandidateInit;
        if (!pc.remoteDescription) { this.pendingIce.push(cand); return; }
        await pc.addIceCandidate(cand).catch(() => {});
      } else if (kind === 'bye') {
        this.dispatch({ type: 'close' });
      }
    } catch { /* una señal rota no tumba el peer: el estado de la conexión manda */ }
  }

  private async flushIce(pc: RTCPeerConnection): Promise<void> {
    const list = this.pendingIce.splice(0);
    for (const c of list) await pc.addIceCandidate(c).catch(() => {});
  }

  private dispatch(ev: PeerEvent): void {
    const { state, action } = reducePeer(this.state, ev, this.opts.role);
    this.state = state;
    if (state.phase === 'live') { this.clearConnectTimer(); this.connectTimeouts = 0; }
    this.runAction(action);
    this.opts.onPhase(state, action);
  }

  private runAction(action: PeerAction): void {
    const pc = this.pc;
    switch (action) {
      case 'start-grace':
        if (this.graceTimer != null) clearTimeout(this.graceTimer);
        this.graceTimer = window.setTimeout(() => { this.graceTimer = null; this.dispatch({ type: 'grace' }); }, DISCONNECT_GRACE_MS);
        break;
      case 'cancel-grace':
        if (this.graceTimer != null) { clearTimeout(this.graceTimer); this.graceTimer = null; }
        break;
      case 'restart-ice':
        if (pc && this.opts.role === 'sharer') { pc.restartIce(); void this.makeOffer(pc, true); }
        break;
      case 'force-relay':
        this.relay = true;
        if (pc && this.opts.role === 'sharer') {
          try { pc.setConfiguration(this.config()); } catch { /* si no deja, el rebuild lo aplica */ }
          pc.restartIce();
          void this.makeOffer(pc, true);
        }
        break;
      default:
        break;
    }
  }

  // ── getStats cada 3 s: RTT, fps, bitrate, pérdida, limitación, par elegido ──

  private startStats(): void {
    if (this.statsTimer != null) clearInterval(this.statsTimer);
    this.statsTimer = window.setInterval(() => { void this.sampleStats(); }, STATS_EVERY_MS);
  }

  async sampleStats(): Promise<PeerStats | null> {
    const pc = this.pc;
    if (!pc || this.closed) return null;
    let report: RTCStatsReport;
    try { report = await pc.getStats(); } catch { return null; }
    let rttMs: number | null = null;
    let fps: number | null = null;
    let lossFraction: number | null = null;
    let limitation: string | null = null;
    let bytes = 0;
    let pair: CandidatePair | null = null;
    let relayUsed = false;
    let selectedPairId: string | null = null;
    const byId = new Map<string, Record<string, unknown>>();
    report.forEach((s: Record<string, unknown>) => { byId.set(String(s.id), s); });
    for (const s of byId.values()) {
      const type = String(s.type);
      if (type === 'transport' && s.selectedCandidatePairId) selectedPairId = String(s.selectedCandidatePairId);
      if (type === 'candidate-pair' && s.state === 'succeeded' && (s as { nominated?: boolean }).nominated && !selectedPairId) selectedPairId = String(s.id);
      if (type === 'outbound-rtp' && s.kind === 'video') {
        if (typeof s.framesPerSecond === 'number') fps = s.framesPerSecond;
        if (typeof s.bytesSent === 'number') bytes += s.bytesSent;
        if (typeof s.qualityLimitationReason === 'string' && s.qualityLimitationReason !== 'none') limitation = s.qualityLimitationReason;
      }
      if (type === 'inbound-rtp' && s.kind === 'video') {
        if (typeof s.framesPerSecond === 'number') fps = s.framesPerSecond;
        if (typeof s.bytesReceived === 'number') bytes += s.bytesReceived;
        const lost = typeof s.packetsLost === 'number' ? s.packetsLost : 0;
        const recv = typeof s.packetsReceived === 'number' ? s.packetsReceived : 0;
        if (recv + lost > 0) lossFraction = lost / (recv + lost);
      }
      if (type === 'remote-inbound-rtp' && s.kind === 'video') {
        if (typeof s.roundTripTime === 'number') rttMs = Math.round(s.roundTripTime * 1000);
        if (typeof s.fractionLost === 'number') lossFraction = s.fractionLost;
      }
    }
    if (selectedPairId) {
      const cp = byId.get(selectedPairId);
      if (cp) {
        if (typeof cp.currentRoundTripTime === 'number') rttMs = Math.round(cp.currentRoundTripTime * 1000);
        const local = byId.get(String(cp.localCandidateId));
        const remote = byId.get(String(cp.remoteCandidateId));
        const lt = local?.candidateType as CandidatePair | undefined;
        const rt = remote?.candidateType as CandidatePair | undefined;
        pair = lt ?? null;
        relayUsed = lt === 'relay' || rt === 'relay';
        if (relayUsed) pair = 'relay';
      }
    }
    const now = Date.now();
    let bitrateKbps: number | null = null;
    if (this.prevBytes && now > this.prevBytes.t) {
      bitrateKbps = Math.round(((bytes - this.prevBytes.bytes) * 8) / (now - this.prevBytes.t));
    }
    this.prevBytes = { t: now, bytes };
    const out: PeerStats = {
      quality: qualityFrom({ rttMs, fps, lossFraction, limitation }),
      rttMs, fps, bitrateKbps, lossFraction, limitation, pair, relayUsed,
    };
    this.opts.onStats(out);
    return out;
  }

  /** Cierra todo. Idempotente. NO para los tracks locales: eso es de la captura (quien la abrió). */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.statsTimer != null) { clearInterval(this.statsTimer); this.statsTimer = null; }
    this.teardownPc();
    this.state = { ...this.state, phase: 'closed' };
  }
}
